/**
 * The one task editor every Work screen opens (Tasks, Calendar, Goals, Team).
 * An existing task saves as you go (each change is its own small save); a
 * new one is added with the button at the bottom.
 *
 * Matthew's rule is on the page: everything has a DATE or a RHYTHM, and
 * everything can be handed off (a how-to link + steps, or an AI employee).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertCircle, BookOpen, Bot, CalendarDays, Check, ExternalLink, Flag, Flame, Handshake, LayoutGrid, ListChecks,
  NotebookPen, Repeat, Rocket, Trash2, UserRound, Users,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Drawer, Input, Select, Textarea, useConfirm, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { TickButton, dueLabel, firstName } from "@/features/command/ui";
import { ChoicePills, Field, PersonPills, TimeInput } from "./fields";
import { RepeatPicker } from "./RepeatPicker";
import { DayPicker } from "./DayPicker";
import { ChecklistEditor } from "./ChecklistEditor";
import { ContactPicker } from "./ContactPicker";
import { HandToAi } from "./HandToAi";
import {
  AREAS, addDays, agentName, joinDue, repeatLabel, sourceOptions, splitDue, taskBody, todayYmd, useLookups, useMe, weekday,
} from "./lib";
import type { Area, WorkTask } from "./types";

export type TaskTarget = WorkTask | { isNew: true; defaults?: Partial<WorkTask> };

const BLANK: WorkTask = {
  id: "", title: "", description: null, contact_id: null, assignee_id: null, due_at: null, status: "pending", priority: "normal",
  completed_at: null, recurrence: null, contact_name: null, source_id: null, goal_id: null, affiliate_id: null, area: null,
  agent_slug: null, agent_task_id: null, handoff_url: null, checklist: null, assignee_name: null, source_name: null,
  source_color: null, goal_title: null, affiliate_name: null,
};

export function TaskDrawer({ target, onClose, onChanged }: { target: TaskTarget | null; onClose: () => void; onChanged: () => void }) {
  const isNew = !!target && "isNew" in target;
  const key = !target ? "none" : isNew ? "new" : (target as WorkTask).id;
  return (
    <Drawer open={!!target} onClose={onClose} title={isNew ? "New task" : "Task"} width="lg">
      {target && <TaskEditor key={key} target={target} onClose={onClose} onChanged={onChanged} />}
    </Drawer>
  );
}

function TaskEditor({ target, onClose, onChanged }: { target: TaskTarget; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { pathname } = useLocation();
  const { people, sources, goals, team, wsTz } = useLookups();
  const { me } = useMe();
  const isNew = "isNew" in target;
  const [t, setT] = useState<WorkTask>(() => (isNew ? { ...BLANK, ...(target.defaults ?? {}) } : (target as WorkTask)));
  const [title, setTitle] = useState(t.title);
  const [notes, setNotes] = useState(t.description ?? "");
  const [howto, setHowto] = useState(t.handoff_url ?? "");
  const initialDue = splitDue(t.due_at, wsTz);
  const [date, setDate] = useState(initialDue.date);
  const [time, setTime] = useState(initialDue.time);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const savedTimer = useRef<number | null>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);

  // A new task starts with the signed-in teammate as its owner.
  useEffect(() => {
    if (isNew && !t.assignee_id && me && people.some((p) => p.id === me.id)) setT((x) => ({ ...x, assignee_id: me.id }));
  }, [isNew, me, people, t.assignee_id]);
  useEffect(() => { if (isNew) titleRef.current?.focus(); }, [isNew]);
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);
  useEffect(() => () => { if (savedTimer.current) window.clearTimeout(savedTimer.current); }, []);

  const commit = async (patch: Partial<WorkTask>) => {
    setT((x) => ({ ...x, ...patch }));
    if (isNew || !t.id) return;
    try {
      await api.patch(`/tasks/${t.id}`, taskBody(patch));
      setSaved(true);
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSaved(false), 1600);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    }
  };

  const setDue = (d: string, tm: string) => {
    setDate(d);
    setTime(tm);
    void commit({ due_at: joinDue(d, tm, wsTz) });
  };

  const create = async () => {
    const name = title.trim();
    if (!name) { toast.error("Give the task a name first"); titleRef.current?.focus(); return; }
    setBusy(true);
    try {
      const body = taskBody({ ...t, title: name, description: notes.trim() || null, handoff_url: howto.trim() || null, due_at: joinDue(date, time, wsTz) });
      for (const k of Object.keys(body)) if (body[k] === null || body[k] === "") delete body[k];
      await api.post("/tasks", body);
      toast.success("Task added");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that task");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Delete this task?",
      description: t.recurrence ? "It repeats. Deleting it stops the routine too." : "This can't be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete that task");
    }
  };

  const toggleDone = () => {
    const done = t.status === "completed";
    void commit({ status: done ? "pending" : "completed" });
    if (!done && t.recurrence && !isNew) toast.success("Done", `The next one is ready (${repeatLabel(t.recurrence)}).`);
  };

  const owner = people.find((p) => p.id === t.assignee_id) ?? null;
  const srcOpts = useMemo(() => sourceOptions(sources), [sources]);
  const goalOpts = goals.filter((g) => g.status === "active" || g.id === t.goal_id);
  const agents = team?.agents ?? [];
  const today = todayYmd();
  const nextMonday = addDays(today, ((8 - weekday(today)) % 7) || 7);
  const due = dueLabel(t.due_at);
  const done = t.status === "completed";
  const noAnchor = !date && !t.recurrence;

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex-1 space-y-5 px-5 py-4">
        {/* Title */}
        <div className="flex items-start gap-3">
          <div className="pt-1.5">
            <TickButton done={done} onToggle={toggleDone} disabled={isNew} />
          </div>
          <div className="min-w-0 flex-1">
            <textarea
              ref={titleRef}
              value={title}
              rows={1}
              onChange={(e) => setTitle(e.target.value.replace(/\n/g, " "))}
              onBlur={() => { const v = title.trim(); if (v && v !== t.title) void commit({ title: v }); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); if (isNew) void create(); } }}
              placeholder="What needs doing?"
              className={cn(
                "w-full resize-none bg-transparent text-lg font-semibold leading-snug text-zinc-50 placeholder:text-zinc-600 focus:outline-none",
                done && "text-zinc-500 line-through",
              )}
            />
            {!isNew && <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
              {!isNew && t.due_at && (
                <span className={cn("font-medium", due.tone === "late" && !done ? "text-rose-300" : due.tone === "today" ? "text-indigo-200" : "text-zinc-500")}>
                  {done ? "Done" : `Due ${due.text}`}
                </span>
              )}
              {t.recurrence && <span className="inline-flex items-center gap-1 text-indigo-300"><Repeat className="h-3 w-3" />{repeatLabel(t.recurrence)}</span>}
              {t.agent_slug && <span className="inline-flex items-center gap-1 text-indigo-300"><Bot className="h-3 w-3" />{agentName(t.agent_slug, team)} does it</span>}
              <span className={cn("ml-auto inline-flex items-center gap-1 text-emerald-300 transition-opacity", saved ? "opacity-100" : "opacity-0")}>
                <Check className="h-3 w-3" /> Saved
              </span>
            </div>}
          </div>
        </div>

        {/* When */}
        <Field label="When" icon={CalendarDays}>
          <div className="flex flex-wrap items-center gap-2">
            <DayPicker value={date} onChange={(d) => setDue(d, d ? time : "")} className="w-48" />
            <TimeInput value={time} onChange={(v) => date && setDue(date, v)} className={cn("w-32", !date && "pointer-events-none opacity-40")} />
            {time && <button type="button" onClick={() => setDue(date, "")} className="text-[11px] text-zinc-500 hover:text-zinc-200">no time</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: "Today", day: today },
              { label: "Tomorrow", day: addDays(today, 1) },
              { label: "Next Monday", day: nextMonday },
            ].map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={() => setDue(c.day, time)}
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                  date === c.day ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-100" : "border-zinc-800 text-zinc-400 hover:text-zinc-100",
                )}
              >
                {c.label}
              </button>
            ))}
            {date && (
              <button type="button" onClick={() => setDue("", "")} className="rounded-full border border-zinc-800 px-2.5 py-0.5 text-[11px] text-zinc-500 hover:text-zinc-200">
                No date
              </button>
            )}
          </div>
          {noAnchor && (
            <div className="flex items-start gap-1.5 rounded-lg bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-200 ring-1 ring-inset ring-amber-500/20">
              <AlertCircle className="mt-px h-3 w-3 shrink-0" />
              Give it a day or make it repeat, so it never gets lost.
            </div>
          )}
        </Field>

        <Field label="Repeat" icon={Repeat}>
          <RepeatPicker value={t.recurrence} dueDate={date || undefined} onChange={(r) => void commit({ recurrence: r })} />
        </Field>

        <Field label="Priority" icon={Flame}>
          <ChoicePills<string>
            size="sm"
            value={t.priority || "normal"}
            onChange={(v) => void commit({ priority: v ?? "normal" })}
            options={[
              { value: "urgent", label: "Urgent", color: "rose" },
              { value: "high", label: "High", color: "amber" },
              { value: "normal", label: "Normal", color: "zinc" },
              { value: "low", label: "Low", color: "slate" },
            ]}
          />
        </Field>

        {/* Who */}
        <Field label="Who owns it" icon={UserRound}>
          <PersonPills people={people} value={t.assignee_id} onChange={(id) => void commit({ assignee_id: id })} />
          <div className="pt-1.5">
            <div className="mb-1 text-[11px] text-zinc-500">Who does the work</div>
            <Select
              value={t.agent_slug ?? ""}
              onChange={(e) => void commit({ agent_slug: e.target.value || null })}
              aria-label="Done by an AI employee under them"
            >
              <option value="">{owner ? `${firstName(owner.name)} does it` : "A person does it"}</option>
              {agents.map((a) => {
                const boss = people.find((p) => p.id === a.manager_id);
                return <option key={a.slug} value={a.slug}>{a.name} does it{boss ? ` (reports to ${firstName(boss.name)})` : ""}</option>;
              })}
              {t.agent_slug && !agents.some((a) => a.slug === t.agent_slug) && (
                <option value={t.agent_slug}>{agentName(t.agent_slug, team)} does it</option>
              )}
            </Select>
            <div className="mt-1 text-[11px] text-zinc-500">
              {team?.ai_available
                ? agents.length ? "An AI employee can do it under them. They still own it." : "No AI employees yet. Make one by chatting with SyncSuite AI."
                : "AI employees appear here once connected. Until then, the owner does it."}
            </div>
          </div>
        </Field>

        <Field label="Notes" icon={NotebookPen}>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => { if (notes.trim() !== (t.description ?? "").trim()) void commit({ description: notes.trim() }); }}
            placeholder="Anything worth knowing"
            className="min-h-[4.5rem]"
          />
        </Field>

        <Field label="Steps" icon={ListChecks}>
          <ChecklistEditor value={t.checklist ?? []} onChange={(items) => void commit({ checklist: items.length ? items : null })} />
        </Field>

        <Field label="How-to" icon={BookOpen} hint="The doc or video that lets anyone do this. With it, anyone (or an AI employee) can take it over.">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
            <Input
              value={howto}
              onChange={(e) => setHowto(e.target.value)}
              onBlur={() => { const v = howto.trim(); if (v !== (t.handoff_url ?? "")) void commit({ handoff_url: v || null }); }}
              placeholder="Paste a link to a doc, a video or a page"
            />
            </div>
            {t.handoff_url && /^https?:\/\//i.test(t.handoff_url) && (
              <a href={t.handoff_url} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center gap-1 rounded-lg border border-zinc-800 px-2.5 text-xs text-zinc-300 hover:text-zinc-100">
                <ExternalLink className="h-3.5 w-3.5" /> Open
              </a>
            )}
          </div>
        </Field>

        {/* Where it belongs */}
        <div className="space-y-4 rounded-2xl border border-zinc-800/80 bg-zinc-900/30 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Where it belongs</div>
          <Field label="Area" icon={LayoutGrid}>
            <ChoicePills<Area> size="sm" clearable value={t.area} onChange={(a) => void commit({ area: a })} options={AREAS.map((a) => ({ value: a.value, label: a.label, color: a.color }))} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Traffic source" icon={Rocket}>
              <Select value={t.source_id ?? ""} onChange={(e) => {
                const s = sources.find((x) => x.id === e.target.value);
                void commit({ source_id: e.target.value || null, source_name: s?.name ?? null, source_color: s?.color ?? null, ...(e.target.value && !t.area ? { area: "growth" as Area } : {}) });
              }}>
                <option value="">None</option>
                {srcOpts.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
            </Field>
            <Field label="Goal it serves" icon={Flag}>
              <Select value={t.goal_id ?? ""} onChange={(e) => void commit({ goal_id: e.target.value || null, goal_title: goals.find((g) => g.id === e.target.value)?.title ?? null })}>
                <option value="">None</option>
                {goalOpts.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="About a person" icon={Users}>
            <ContactPicker id={t.contact_id} name={t.contact_name} onChange={(id, name) => void commit({ contact_id: id, contact_name: name })} />
          </Field>
          {t.affiliate_id && (
            <Field label="Affiliate" icon={Handshake}>
              <div className="flex items-center gap-2">
                <Link to={`/affiliates/${t.affiliate_id}`} state={{ from: pathname }} className="text-sm text-zinc-100 hover:text-indigo-200 hover:underline">
                  {t.affiliate_name || "Open the affiliate"}
                </Link>
                <button type="button" onClick={() => void commit({ affiliate_id: null, affiliate_name: null })} className="text-[11px] text-zinc-500 hover:text-zinc-200">unlink</button>
              </div>
            </Field>
          )}
        </div>

        {!isNew && <HandToAi task={t} team={team} ownerName={owner?.name ?? null} onHanded={(slug, id) => { setT((x) => ({ ...x, agent_slug: slug, agent_task_id: id, status: "in_progress" })); onChanged(); }} />}
      </div>

      <div className="sticky bottom-0 flex items-center gap-2 border-t border-zinc-800 bg-zinc-950/95 px-5 py-3 backdrop-blur">
        {!isNew && (
          <Button type="button" variant="ghost" size="sm" onClick={remove} leftIcon={<Trash2 className="h-3.5 w-3.5" />} className="text-rose-300 hover:bg-rose-500/10 hover:text-rose-200">
            Delete
          </Button>
        )}
        <div className="flex-1" />
        {isNew ? (
          <>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="button" size="sm" loading={busy} onClick={create}>Add task</Button>
          </>
        ) : (
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>Close</Button>
        )}
      </div>
    </div>
  );
}
